import { useState } from 'react';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyTableState,
  ErrorState,
  PageContainer,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
  useDebouncedValue,
  type DataTableColumn,
} from '@startupquickstart/frontend';
import { formatDistanceToNow } from 'date-fns';
import { Check, KeyRound, X } from 'lucide-react';
import {
  ACCESS_REQUEST_REVIEW_ACTIONS,
  ACCESS_REQUEST_STATUSES,
  useAccessRequests,
  useReviewAccessRequest,
  type AccessRequest,
  type AccessRequestReviewAction,
  type AccessRequestStatus,
} from '@/api/access-requests';
import { AccessRequestStatusBadge } from '@/components/access-requests/AccessRequestStatusBadge';

type StatusFilter = AccessRequestStatus | 'all';

/** Debounce applied to the search box before it hits the API. */
const SEARCH_DEBOUNCE_MS = 300;

/** A review action awaiting user confirmation in the dialog. */
interface PendingReview {
  request: AccessRequest;
  action: AccessRequestReviewAction;
}

const CONFIRM_COPY: Record<AccessRequestReviewAction, { title: string; description: string; cta: string }> = {
  approve: {
    title: 'Approve access?',
    description:
      'The requester is emailed their portal link right away and whitelisted for future requests.',
    cta: 'Approve access',
  },
  deny: {
    title: 'Deny access?',
    description:
      'The request is closed and any outstanding portal link is revoked. The requester is not emailed.',
    cta: 'Deny access',
  },
};

/**
 * Access Requests admin page (internal only).
 *
 * Lists every hotel-portal access request from the marketing site — pending
 * (needs review), approved, and denied — with inline approve/deny actions.
 * Approving emails the requester their portal link; denying revokes any
 * outstanding link. The same requests can also be reviewed straight from the
 * staff notification emails.
 */
export function AccessRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ACCESS_REQUEST_STATUSES.pending);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pendingReview, setPendingReview] = useState<PendingReview | null>(null);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const { data, isLoading, error, refetch } = useAccessRequests({
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    page,
  });
  const review = useReviewAccessRequest();

  const pendingCount = data?.counts?.[ACCESS_REQUEST_STATUSES.pending] ?? 0;

  const handleFilterChange = (value: string) => {
    setStatusFilter(value as StatusFilter);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleConfirm = () => {
    if (!pendingReview || review.isPending) return;
    const { request, action } = pendingReview;
    review.mutate(
      { id: request.id, action },
      {
        onSuccess: result => {
          setPendingReview(null);
          if (!result.transitioned) {
            toast.success(`Already ${result.request.status.toLowerCase()} — nothing re-sent.`);
          } else if (action === ACCESS_REQUEST_REVIEW_ACTIONS.approve) {
            toast.success(`Access approved — ${request.email} has been emailed their portal link.`);
          } else {
            toast.success(`Access denied for ${request.email}.`);
          }
        },
        onError: err => {
          toast.error(err.message || 'Failed to update the access request.');
        },
      },
    );
  };

  const columns: DataTableColumn<AccessRequest>[] = [
    {
      key: 'email',
      label: 'Requester',
      format: (_, request) => (
        <div>
          <p className="font-medium">{request.email}</p>
          <p className="text-sm text-muted-foreground">{request.domain}</p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      format: (_, request) => <AccessRequestStatusBadge status={request.status} />,
    },
    {
      key: 'requestedAt',
      label: 'Requested',
      format: value => formatDistanceToNow(new Date(value), { addSuffix: true }),
      hideOnMobile: true,
    },
    {
      key: 'reviewedAt',
      label: 'Reviewed',
      format: (_, request) =>
        request.reviewedAt ? (
          <div>
            <p className="text-sm">
              {formatDistanceToNow(new Date(request.reviewedAt), { addSuffix: true })}
            </p>
            {request.reviewedByEmail && (
              <p className="text-sm text-muted-foreground">{request.reviewedByEmail}</p>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
      hideOnMobile: true,
    },
    {
      key: 'accessCount',
      label: 'Portal visits',
      align: 'right',
      format: value => <span className="tabular-nums">{value}</span>,
      hideOnMobile: true,
    },
    {
      key: 'id',
      label: 'Actions',
      align: 'right',
      disableRowClick: true,
      format: (_, request) => (
        <div className="flex justify-end gap-2">
          {request.status !== ACCESS_REQUEST_STATUSES.approved && (
            <Button
              size="sm"
              variant="outline"
              className="text-green-700 hover:text-green-800"
              onClick={() =>
                setPendingReview({ request, action: ACCESS_REQUEST_REVIEW_ACTIONS.approve })
              }
            >
              <Check className="mr-1 h-4 w-4" />
              Approve
            </Button>
          )}
          {request.status !== ACCESS_REQUEST_STATUSES.denied && (
            <Button
              size="sm"
              variant="outline"
              className="text-red-700 hover:text-red-800"
              onClick={() =>
                setPendingReview({ request, action: ACCESS_REQUEST_REVIEW_ACTIONS.deny })
              }
            >
              <X className="mr-1 h-4 w-4" />
              {request.status === ACCESS_REQUEST_STATUSES.approved ? 'Revoke' : 'Deny'}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const statusControl = (
    <Tabs value={statusFilter} onValueChange={handleFilterChange}>
      <TabsList>
        <TabsTrigger value={ACCESS_REQUEST_STATUSES.pending} className="flex items-center gap-2">
          Pending
          {pendingCount > 0 && (
            <Badge variant="warning" className="ml-1 h-5 min-w-5 px-1.5">
              {pendingCount}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value={ACCESS_REQUEST_STATUSES.approved}>Approved</TabsTrigger>
        <TabsTrigger value={ACCESS_REQUEST_STATUSES.denied}>Denied</TabsTrigger>
        <TabsTrigger value="all">All</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const confirmCopy = pendingReview ? CONFIRM_COPY[pendingReview.action] : null;

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Access Requests</h1>
          <p className="text-muted-foreground">
            Hotel-portal access requests from the marketing site — approve to email the requester
            their portal link, deny to close the request
          </p>
        </div>

        {error ? (
          <ErrorState
            title="Failed to load access requests"
            error={error}
            action={{ label: 'Try again', onClick: () => refetch() }}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data?.requests ?? []}
            loading={isLoading}
            searchable
            searchPlaceholder="Search by email or domain…"
            onSearch={handleSearch}
            toolbarLeft={statusControl}
            pagination={
              data?.pagination
                ? {
                    page: data.pagination.page,
                    limit: data.pagination.limit,
                    total: data.pagination.total,
                    totalPages: Math.max(
                      1,
                      Math.ceil(data.pagination.total / data.pagination.limit),
                    ),
                    hasNext: data.pagination.hasNext,
                    hasPrev: data.pagination.hasPrev,
                  }
                : undefined
            }
            onPageChange={setPage}
            emptyState={
              <EmptyTableState
                icon={<KeyRound className="h-12 w-12" />}
                title="No access requests"
                description={
                  statusFilter === ACCESS_REQUEST_STATUSES.pending
                    ? 'There are no pending access requests to review'
                    : 'No access requests found'
                }
              />
            }
            skeletonRows={5}
          />
        )}
      </div>

      <Dialog
        open={pendingReview !== null}
        onOpenChange={open => {
          if (!open) setPendingReview(null);
        }}
      >
        <DialogContent>
          {pendingReview && confirmCopy && (
            <>
              <DialogHeader>
                <DialogTitle>{confirmCopy.title}</DialogTitle>
                <DialogDescription>
                  <span className="font-medium text-foreground">{pendingReview.request.email}</span>{' '}
                  ({pendingReview.request.domain}) — {confirmCopy.description}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <Button variant="outline" onClick={() => setPendingReview(null)}>
                  Cancel
                </Button>
                <Button
                  variant={
                    pendingReview.action === ACCESS_REQUEST_REVIEW_ACTIONS.deny
                      ? 'destructive'
                      : 'default'
                  }
                  disabled={review.isPending}
                  onClick={handleConfirm}
                >
                  {review.isPending ? 'Working…' : confirmCopy.cta}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
